// src/pages/AddArtwork.jsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { getOrCreateUnclassifiedCollection } from "../lib/unclassifiedCollection";

import ArtworkDetailTab from "../components/artwork/tabs/ArtworkDetailTab";
import ArtworkSpecificationsTab from "../components/artwork/tabs/ArtworkSpecificationsTab";
import ArtworkProvenanceTab from "../components/artwork/tabs/ArtworkProvenanceTab";
import ArtworkDocumentsTab from "../components/artwork/tabs/ArtworkDocumentsTab";
import ArtworkLocationTab from "../components/artwork/tabs/ArtworkLocationTab";
import ArtworkNFTTab from "../components/artwork/tabs/ArtworkNFTTab";
import ArtworkImageManager from "../components/artwork/ArtworkImageManager";
import LocationModal from "../components/artwork/modals/LocationModal";
import UseMyPhoneModal from "../components/artwork/modals/UseMyPhoneModal";

const numericFields = [
  "value",
  "price",
  "sale_price",
  "estimated_value",
  "acquisition_price",
  "price_low",
  "price_high",
  "height",
  "width",
  "depth",
  "framed_height",
  "framed_width",
  "framed_depth",
  "paper_height",
  "paper_width",
  "dimensions_height",
  "dimensions_width",
  "dimensions_depth",
  "valuation_estimated_value",
  "valuation_confidence_score",
  "valuation_market_value",
  "valuation_consensus",
  "current_estimated_value",
  "weight",
  "auction_hammer_price",
  "auction_fees",
];

const dateFields = [
  "acquisition_date",
  "last_estimation_date",
  "last_valuation_date",
  "expert_date",
  "auction_sale_date",
  "visited_at",
];

const toNumericOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const normalized =
    typeof value === "string" ? value.replace(",", ".").trim() : value;
  if (normalized === "") return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const emptyToNull = (value) => {
  if (value === "" || value === undefined) return null;
  return value;
};

const cleanArtworkPayload = (formData) => {
  const specs = formData.specifications || {};
  const dimensions = specs.dimensions || {};
  const framedSize = specs.framedSize || {};
  const paperSize = specs.paperSize || {};
  const acquisitionDetails = formData.acquisitionDetails || {};

  const payload = {
    ...formData,

    editionInfo: formData.isEdition ? formData.editionInfo : null,

    images: Array.isArray(formData.images) ? formData.images : [],
    tags: Array.isArray(formData.tags) ? formData.tags : [],

    provenance: Array.isArray(formData.provenance)
      ? JSON.stringify(formData.provenance)
      : formData.provenance || null,

    documents: Array.isArray(formData.documents)
      ? JSON.stringify(formData.documents)
      : formData.documents || null,

    location_id: formData.location_id || null,
    blockchainMetadata: formData.blockchainMetadata || null,

    acquisitionDetails,

    acquisition_type: acquisitionDetails.type || null,
    acquisition_date: acquisitionDetails.date || null,
    acquisition_source: acquisitionDetails.source || null,
    acquisition_price: acquisitionDetails.price,

    unit: dimensions.unit || formData.unit || "cm",
    dimensions_unit: dimensions.unit || formData.dimensions_unit || "cm",

    height: dimensions.height,
    width: dimensions.width,
    depth: dimensions.depth,

    dimensions_height: dimensions.height,
    dimensions_width: dimensions.width,
    dimensions_depth: dimensions.depth,

    framed_height: framedSize?.height ?? null,
    framed_width: framedSize?.width ?? null,
    framed_depth: framedSize?.depth ?? null,

    paper_height: paperSize?.height ?? null,
    paper_width: paperSize?.width ?? null,

    duration: specs.duration || null,
  };

  delete payload.current_location;

  numericFields.forEach((field) => {
    if (field in payload) {
      payload[field] = toNumericOrNull(payload[field]);
    }
  });

  dateFields.forEach((field) => {
    if (field in payload) {
      payload[field] = emptyToNull(payload[field]);
    }
  });

  return payload;
};

const AddArtwork = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();

  const preselectedCollectionId =
    location.state?.preselectedCollectionId || null;

  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(
    preselectedCollectionId
  );
  const [locations, setLocations] = useState([]);
  const [showPhoneQr, setShowPhoneQr] = useState(false); // passerelle QR : prendre la photo avec son téléphone

  const [formData, setFormData] = useState({
    title: "",
    artist: "",
    year: "",
    medium: "",
    typeOfArt: "Peinture",
    availability: "Available",
    isEdition: false,
    editionInfo: "",
    specifications: {
      dimensions: { height: "", width: "", depth: "", unit: "cm" },
      framedSize: null,
      paperSize: null,
      duration: "",
    },
    acquisitionDetails: {
      type: "",
      date: "",
      source: "",
      price: "",
    },
    value: null,
    condition: "",
    conditionNotes: "",
    images: [],
    provenance: [],
    documents: [],
    tags: [],
    location_id: null,
    blockchainMetadata: null,
  });

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);

  const tabs = [
    { name: t("addArtwork.tabs.details"), id: "details" },
    { name: t("addArtwork.tabs.specifications"), id: "specifications" },
    { name: t("addArtwork.tabs.provenance"), id: "provenance" },
    { name: t("addArtwork.tabs.documents"), id: "documents" },
    { name: t("addArtwork.tabs.location"), id: "location" },
    { name: t("addArtwork.tabs.nft"), id: "nft" },
  ];

  const handleCreateCollection = async () => {
    const name = prompt(t("addArtwork.collection.prompt"));
    if (!name || !user?.id) return;

    try {
      const { data, error } = await supabase
        .from("collections")
        .insert([{ title: name, client_id: user.id }])
        .select()
        .single();

      if (error) throw error;

      setCollections((prev) => [...prev, data]);
      setSelectedCollection(data.id);
      alert(t("addArtwork.collection.created"));
    } catch (err) {
      console.error("Erreur création collection:", err);
      alert(t("addArtwork.collection.createError"));
    }
  };

  useEffect(() => {
    if (!user?.id) return;

    const fetchCollections = async () => {
      const { data } = await supabase
        .from("collections")
        .select("*")
        .eq("client_id", user.id);

      setCollections(data || []);

      if (!preselectedCollectionId && data?.length) {
        setSelectedCollection(data[0].id);
      }
    };

    const fetchLocations = async () => {
      const { data } = await supabase
        .from("locations")
        .select("*")
        .eq("user_id", user.id);

      setLocations(data || []);
    };

    fetchCollections();
    fetchLocations();
  }, [user?.id, preselectedCollectionId]);

  const handleAddImages = async (newImages) => {
    if (!user) return;
    setSaveInProgress(true);

    try {
      const uploaded = await Promise.all(
        newImages.map(async (file) => {
          const ext = file.name.split(".").pop();
          const fileName = `${Date.now()}-${Math.random()
            .toString(36)
            .substring(2)}.${ext}`;
          const path = `${user.id}/${fileName}`;

          const { error } = await supabase.storage
            .from("artworks-images")
            .upload(path, file);

          if (error) throw error;

          const { data: publicURL } = supabase.storage
            .from("artworks-images")
            .getPublicUrl(path);

          return {
            id: Date.now() + Math.random(),
            path: publicURL.publicUrl,
            url: publicURL.publicUrl,
            isPrimary: false,
          };
        })
      );

      setFormData((prev) => {
        const current = prev.images || [];
        const hasPrimary = current.some((img) => img.isPrimary);

        if (!hasPrimary && uploaded.length > 0) {
          uploaded[0].isPrimary = true;
        }

        return { ...prev, images: [...current, ...uploaded] };
      });
    } catch (e) {
      console.error("Erreur upload image:", e);
      alert(t("addArtwork.uploadError"));
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleSubmit = async () => {
    if (!user) return;
    setSaveInProgress(true);

    try {
      let collectionId = selectedCollection;

      if (!collectionId) {
        collectionId = await getOrCreateUnclassifiedCollection(user.id);
      }

      const cleaned = cleanArtworkPayload(formData);

      const { error } = await supabase
        .from("artworks")
        .insert([
          {
            ...cleaned,
            client_id: user.id,
            collection_id: collectionId,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      alert(t("addArtwork.success"));
      navigate(`/collections/${collectionId}`, { replace: true });
    } catch (e) {
      console.error("Erreur création œuvre:", e);
      alert(t("addArtwork.saveError", { message: e.message }));
    } finally {
      setSaveInProgress(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <header className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{t("addArtwork.title")}</h1>
      </header>

      {/* Voies plus ergonomiques (même esprit qu'onboarding : scan doc / pile WhatsApp) */}
      {(() => {
        const lang = String(i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2).toLowerCase();
        const C = {
          fr: { intro: "Plus rapide qu'à la main :", doc: "Depuis un document (facture, certificat)", batch: "Plusieurs fichiers / photos (WhatsApp…)", or: "ou continuez la saisie manuelle ci-dessous" },
          en: { intro: "Faster than by hand:", doc: "From a document (invoice, certificate)", batch: "Several files / photos (WhatsApp…)", or: "or continue entering manually below" },
          de: { intro: "Schneller als von Hand:", doc: "Aus einem Dokument (Rechnung, Zertifikat)", batch: "Mehrere Dateien / Fotos (WhatsApp…)", or: "oder erfassen Sie unten manuell weiter" },
        }[lang] || null;
        const c = C || { intro: "Plus rapide qu'à la main :", doc: "Depuis un document (facture, certificat)", batch: "Plusieurs fichiers / photos (WhatsApp…)", or: "ou continuez la saisie manuelle ci-dessous" };
        return (
          <div style={{ border: "1px solid #e6d9b3", background: "#faf6ec", borderRadius: 12, padding: "12px 14px", marginBottom: 24 }}>
            <div style={{ fontSize: 13, color: "#7a5f1a", fontWeight: 600, marginBottom: 8 }}>{c.intro}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <button type="button" onClick={() => navigate("/create-from-document")}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 999, border: "1px solid #c9a961", background: "#c9a961", color: "#1a1a1a", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                {c.doc}
              </button>
              <button type="button" onClick={() => navigate("/import-batch")}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 999, border: "1px solid #d8d3c8", background: "#fff", color: "#1a1a1a", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                {c.batch}
              </button>
            </div>
            <div style={{ fontSize: 12, color: "#a29a8a", marginTop: 8 }}>{c.or}</div>
          </div>
        );
      })()}

      <div className="mb-6">
        <ArtworkImageManager
          images={formData.images}
          readOnly={false}
          onAddImages={handleAddImages}
          onSetPrimaryImage={(id) =>
            setFormData((prev) => ({
              ...prev,
              images: prev.images.map((img) => ({
                ...img,
                isPrimary: img.id === id,
              })),
            }))
          }
          onRemoveImage={(id) =>
            setFormData((prev) => {
              const updated = prev.images.filter((img) => img.id !== id);

              if (
                prev.images.find((i) => i.id === id)?.isPrimary &&
                updated.length
              ) {
                updated[0].isPrimary = true;
              }

              return { ...prev, images: updated };
            })
          }
        />

        <button
          type="button"
          onClick={() => setShowPhoneQr(true)}
          className="mt-3 text-sm flex items-center gap-1 hover:underline"
          style={{ color: "#8a6d1f" }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
          </svg>
          {t("usePhone.title")}
        </button>

        {/* Pas encore d'artwork_id ici (fiche pas créée) : le mobile crée une
            pré-fiche temporaire (actionType=new_artwork) juste pour porter la
            photo, on récupère l'image dans le formulaire en cours puis on
            supprime cette pré-fiche pour ne rien dupliquer. */}
        <UseMyPhoneModal
          open={showPhoneQr}
          onClose={() => setShowPhoneQr(false)}
          actionType="new_artwork"
          onCompleted={async (row) => {
            const tempId = row?.result_path;
            if (!tempId) return;
            try {
              const { data } = await supabase
                .from("artworks")
                .select("images")
                .eq("id", tempId)
                .maybeSingle();
              const capturedImages = data?.images || [];
              if (capturedImages.length) {
                setFormData((prev) => {
                  const current = prev.images || [];
                  const hasPrimary = current.some((img) => img.isPrimary);
                  const incoming = capturedImages.map((img, i) => ({
                    ...img,
                    isPrimary: !hasPrimary && i === 0,
                  }));
                  return { ...prev, images: [...current, ...incoming] };
                });
              }
            } finally {
              await supabase.from("artworks").delete().eq("id", tempId);
            }
          }}
        />
      </div>

      <div className="mb-6">
        <label
          htmlFor="collection"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {t("addArtwork.collection.label")}
        </label>

        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <select
            id="collection"
            name="collection"
            value={selectedCollection || ""}
            onChange={(e) => setSelectedCollection(e.target.value || null)}
            className="w-full md:w-auto flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
          >
            <option value="">
              {t("addArtwork.collection.auto")}
            </option>

            {collections.map((col) => (
              <option key={col.id} value={col.id}>
                {col.title || t("addArtwork.collection.unnamed", { id: col.id })}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={handleCreateCollection}
            className="btn-artsy btn-primary-artsy whitespace-nowrap"
          >
            {t("addArtwork.collection.create")}
          </button>
        </div>
      </div>

      <div className="border-b mb-4">
        <nav className="flex space-x-8 overflow-x-auto">
          {tabs.map((tab, i) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setSelectedTabIndex(i)}
              className={`pb-2 whitespace-nowrap flex-shrink-0 ${
                selectedTabIndex === i
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      <div className="py-6">
        {selectedTabIndex === 0 && (
          <ArtworkDetailTab
            formData={formData}
            setFormData={setFormData}
            readOnly={false}
          />
        )}

        {selectedTabIndex === 1 && (
          <ArtworkSpecificationsTab
            formData={formData}
            setFormData={setFormData}
            readOnly={false}
          />
        )}

        {selectedTabIndex === 2 && (
          <ArtworkProvenanceTab
            formData={formData}
            readOnly={false}
            onAddProvenance={() => {
              const newItem = {
                id: Date.now(),
                date: new Date().toISOString(),
                description: "",
                location: "",
              };

              setFormData((prev) => ({
                ...prev,
                provenance: [...(prev.provenance || []), newItem],
              }));
            }}
            onRemoveProvenance={(id) =>
              setFormData((prev) => ({
                ...prev,
                provenance: prev.provenance.filter((p) => p.id !== id),
              }))
            }
          />
        )}

        {selectedTabIndex === 3 && (
          <ArtworkDocumentsTab
            formData={formData}
            readOnly={false}
            onAddDocument={() => {
              const newDoc = { id: Date.now(), title: "", file: "" };

              setFormData((prev) => ({
                ...prev,
                documents: [...(prev.documents || []), newDoc],
              }));
            }}
            onRemoveDocument={(id) =>
              setFormData((prev) => ({
                ...prev,
                documents: prev.documents.filter((doc) => doc.id !== id),
              }))
            }
          />
        )}

        {selectedTabIndex === 4 && (
          <ArtworkLocationTab
            formData={formData}
            locations={locations}
            onChangeLocation={() => setShowLocationModal(true)}
            readOnly={false}
          />
        )}

        {selectedTabIndex === 5 && (
          <ArtworkNFTTab
            formData={formData}
            setFormData={setFormData}
            readOnly={false}
          />
        )}
      </div>

      <div className="flex justify-end mt-8">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saveInProgress}
          className="btn-artsy btn-primary-artsy"
        >
          {saveInProgress ? t("addArtwork.saving") : t("addArtwork.submit")}
        </button>
      </div>

      {showLocationModal && (
        <LocationModal
          artwork={formData}
          locations={locations}
          onChangeLocation={(id) => {
            setFormData((prev) => ({ ...prev, location_id: id }));
            setShowLocationModal(false);
          }}
          onClose={() => setShowLocationModal(false)}
        />
      )}
    </div>
  );
};

export default AddArtwork;